import { checkPermissions, useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { Icon, InitialsAvatar, useIsMobile } from "@elit-hair/elit-ui";
import { useNavigate } from "@tanstack/react-router";
import {
    AnimatePresence,
    MotionValue,
    type SpringOptions,
    motion,
    useMotionValue,
    useSpring,
    useTransform,
} from "motion/react";
import { Children, ReactNode, cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    TbBed,
    TbCheck,
    TbChevronRight,
    TbExternalLink,
    TbMicrophone,
    TbMicrophoneOff,
    TbPhoneOff,
    TbSettings,
    TbSpeakerphone,
} from "react-icons/tb";

import {
    createCrmAction,
    createEmailAction,
    createWhatsappAction,
} from "@/components/call-dock/call-dock-channel-actions";
import { CallDurationTimer } from "@/components/call-duration-timer";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { PERMISSIONS } from "@/constants/permissions";
import { useCapabilities } from "@/features/audio";
import { cn } from "@/lib/utils";
import { useCall } from "@/providers/hooks/use-call";
import { soundService } from "@/services/soundService";
import { APP_ROUTES_TO } from "@/utils/routes";

type DockItemData = {
    icon: ReactNode;
    label: string;
    onClick?: () => void;
    className?: string;
    /** When set, the item renders as a semantic <a> (for external URLs or protocol handlers). */
    href?: string;
    target?: string;
    rel?: string;
};

type DockItemProps = {
    className?: string;
    children: ReactNode;
    onClick?: () => void;
    href?: string;
    target?: string;
    rel?: string;
    mouseX: MotionValue<number>;
    spring: SpringOptions;
    distance: number;
    baseItemSize: number;
    magnification: number;
};

/**
 * Renders a dock item whose size animates based on the pointer's horizontal distance to the item and forwards a hover motion value to its children.
 *
 * @param children - Child elements; if a child is a React element it will receive an `isHovered: MotionValue<number>` prop that transitions between `0` and `1`.
 * @param className - Additional CSS classes applied to the item container.
 * @param onClick - Click handler invoked when the item is activated.
 * @param mouseX - A MotionValue<number> representing the current pointer X position used to compute proximity-based scaling.
 * @param spring - Spring configuration used to animate the item's size.
 * @param distance - Horizontal distance (in pixels) from the item's center at which the item scales down to `baseItemSize`.
 * @param magnification - Target size (in pixels) applied when the pointer is centered over the item.
 * @param baseItemSize - Default size (in pixels) of the item when not magnified.
 * @returns The dock item element with animated sizing and hover propagation to children.
 */

const DOCK_ITEM_BASE_CLASSES =
    "relative inline-flex items-center justify-center rounded-xl bg-background/40 backdrop-blur-sm border border-border/30 shadow-md";

function DockItem({
    children,
    className = "",
    onClick,
    href,
    target,
    rel,
    mouseX,
    spring,
    distance,
    magnification,
    baseItemSize,
}: DockItemProps) {
    const anchorRef = useRef<HTMLAnchorElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const isHovered = useMotionValue(0);

    const mouseDistance = useTransform(mouseX, (val) => {
        const el = href ? anchorRef.current : buttonRef.current;
        const rect = el?.getBoundingClientRect() ?? { x: 0, width: baseItemSize };
        return val - rect.x - baseItemSize / 2;
    });

    const targetSize = useTransform(
        mouseDistance,
        [-distance, 0, distance],
        [baseItemSize, magnification, baseItemSize]
    );
    const size = useSpring(targetSize, spring);

    const commonProps = {
        style: { width: size, height: size },
        onHoverStart: () => isHovered.set(1),
        onHoverEnd: () => isHovered.set(0),
        onFocus: () => isHovered.set(1),
        onBlur: () => isHovered.set(0),
        className: cn(DOCK_ITEM_BASE_CLASSES, className),
        tabIndex: 0,
        "aria-haspopup": "true" as const,
    };

    if (href) {
        return (
            <motion.a ref={anchorRef} {...commonProps} href={href} target={target} rel={rel}>
                {Children.map(children, (child) =>
                    isValidElement<{ isHovered?: MotionValue<number> }>(child)
                        ? cloneElement(child, { isHovered })
                        : child
                )}
            </motion.a>
        );
    }

    return (
        <motion.button ref={buttonRef} {...commonProps} onClick={onClick}>
            {Children.map(children, (child) =>
                isValidElement<{ isHovered?: MotionValue<number> }>(child) ? cloneElement(child, { isHovered }) : child
            )}
        </motion.button>
    );
}

type DockLabelProps = {
    className?: string;
    children: ReactNode;
    isHovered?: MotionValue<number>;
};

/**
 * Renders a tooltip-like label positioned above a dock item when the provided hover motion value indicates the item is hovered.
 *
 * @param children - Label content to display inside the tooltip.
 * @param className - Additional CSS classes applied to the tooltip container.
 * @param isHovered - A MotionValue-like object that emits `1` when hovered and `0` otherwise; the tooltip is shown when this value becomes `1`.
 * @returns The tooltip element when visible, otherwise null.
 */
function DockLabel({ children, className = "", isHovered }: DockLabelProps) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!isHovered) return;
        const unsubscribe = isHovered.on("change", (latest) => {
            setIsVisible(latest === 1);
        });
        return () => unsubscribe();
    }, [isHovered]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0, y: 0 }}
                    animate={{ opacity: 1, y: -10 }}
                    exit={{ opacity: 0, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={cn(
                        className,
                        "absolute -top-8 left-1/2 w-fit whitespace-nowrap rounded-md",
                        "border border-border/50 bg-background/90 backdrop-blur-sm",
                        "px-2 py-1 text-xs text-foreground shadow-lg"
                    )}
                    role="tooltip"
                    style={{ x: "-50%" }}
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

type DockIconProps = {
    className?: string;
    children: ReactNode;
    isHovered?: MotionValue<number>;
};

/**
 * Renders a centered container for a dock icon.
 *
 * @param className - Additional class names to apply to the container
 * @returns A div element that centers `children` and applies `className`
 */
function DockIcon({ children, className = "" }: DockIconProps) {
    return <div className={cn("flex items-center justify-center", className)}>{children}</div>;
}

/**
 * Renders a floating call control dock with quick actions and device settings while a call is active.
 *
 * Provides buttons for viewing the record, sending email or WhatsApp, opening the CRM record, toggling mute with audio level visualization, opening audio device settings, and hanging up. Displays a call duration timer and a settings popover for selecting input/output devices; the dock is hidden when no call is active.
 *
 * @returns The dock element when a call is active or becoming active, `null` otherwise.
 */
export function CallDock() {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const { userData } = useAuth();
    const canViewHotelCapacity = checkPermissions(userData?.permissionCodes ?? [], [PERMISSIONS.CALL_HOTEL_BASE]);
    const { canSelectMicrophone, canSelectSpeaker } = useCapabilities();
    const showDeviceSettings = canSelectMicrophone || canSelectSpeaker;
    const {
        isCallActive,
        callStartTime,
        callButtonCooldown,
        handleHangupCall,
        currentRecordInformation,
        currentCallData,
        handleMuteMic,
        isMuted,
        audioLevel,
        selectedBrand,
        setInputDevice,
        setOutputDevice,
    } = useCall();
    const [isVisible, setIsVisible] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [hoveredDeviceType, setHoveredDeviceType] = useState<"input" | "output" | null>(null);
    const [mediaDevices, setMediaDevices] = useState<MediaDeviceInfo[]>([]);
    const settingsRef = useRef<HTMLDivElement>(null);
    const settingsButtonRef = useRef<HTMLDivElement>(null);

    // Use localStorage to persist device selection (same as settings page)
    const [selectedInputDeviceId, setSelectedInputDeviceId] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key,
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.default
    );

    const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.key,
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.default
    );

    const isActive = isCallActive();

    // Smooth animation when call becomes active
    useEffect(() => {
        if (isActive) {
            // Small delay for smooth pop-up effect
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 50);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [isActive]);

    // Load media devices (unavailable on non-secure origins, e.g. http://LAN-IP)
    useEffect(() => {
        const mediaDevicesApi = navigator.mediaDevices;
        if (!mediaDevicesApi) return;

        const loadDevices = async () => {
            let stream: MediaStream | null = null;
            try {
                // Request permission first to get device labels
                stream = await mediaDevicesApi.getUserMedia({ audio: true });
                const devices = await mediaDevicesApi.enumerateDevices();
                setMediaDevices(devices);
            } catch (error) {
                console.error("Error loading media devices:", error);
            } finally {
                // Always stop/release the stream to prevent microphone stream leaks
                if (stream) {
                    stream.getTracks().forEach((track) => track.stop());
                }
            }
        };

        if (isSettingsOpen) {
            loadDevices();
        }

        // Listen for device changes
        const handleDeviceChange = () => {
            mediaDevicesApi.enumerateDevices().then(setMediaDevices);
        };

        mediaDevicesApi.addEventListener("devicechange", handleDeviceChange);
        return () => {
            mediaDevicesApi.removeEventListener("devicechange", handleDeviceChange);
        };
    }, [isSettingsOpen]);

    // Close settings popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                event.target instanceof Node &&
                settingsRef.current &&
                !settingsRef.current.contains(event.target) &&
                settingsButtonRef.current &&
                !settingsButtonRef.current.contains(event.target)
            ) {
                setIsSettingsOpen(false);
                setHoveredDeviceType(null);
            }
        };

        if (isSettingsOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isSettingsOpen]);

    // Get record name
    const recordName = currentRecordInformation?.Full_Name || currentCallData?.calledName || t("unknown");

    // Get front image (image1 is typically the front image)
    const frontImage =
        currentRecordInformation?.Bild_1 && typeof currentRecordInformation.Bild_1 === "string"
            ? currentRecordInformation.Bild_1
            : null;

    const navigate = useNavigate();
    const handleRecordClick = () => {
        if (!isActive) return;
        navigate({ to: "/" });
    };

    const handleHotelCapacityClick = () => {
        if (!isActive) return;
        navigate({ to: APP_ROUTES_TO.HOTEL_CAPACITY });
    };

    const channelActions = useMemo(
        () => ({
            email: createEmailAction(currentRecordInformation, currentCallData, isActive),
            whatsapp: createWhatsappAction(currentRecordInformation, currentCallData, isActive),
            crm: createCrmAction(currentRecordInformation, currentCallData, selectedBrand, isActive),
        }),
        [currentRecordInformation, currentCallData, selectedBrand, isActive]
    );

    // Dock configuration
    const spring: SpringOptions = { mass: 0.1, stiffness: 150, damping: 12 };
    const magnification = isMobile ? 44 : 70;
    const distance = isMobile ? 0 : 200;
    const panelHeight = isMobile ? 56 : 64;
    const baseItemSize = isMobile ? 44 : 50;

    const mouseX = useMotionValue(Infinity);
    const isHovered = useMotionValue(0);

    const maxHeight = useMemo(() => Math.max(256, magnification + magnification / 2 + 4), [magnification]);
    const heightRow = useTransform(isHovered, [0, 1], [panelHeight, maxHeight]);
    const height = useSpring(heightRow, spring);
    const dockHeight = isMobile ? panelHeight : height;

    // Prepare dock items
    const dockItems: DockItemData[] = useMemo(() => {
        const items: DockItemData[] = [
            {
                icon: frontImage ? (
                    <div
                        className={cn(
                            "flex items-center justify-center rounded-full overflow-hidden bg-muted/20",
                            isMobile ? "w-10 h-10" : "w-12 h-12"
                        )}
                    >
                        <img src={frontImage} alt={recordName} className="w-full h-full object-cover" />
                    </div>
                ) : (
                    <InitialsAvatar name={recordName} className="rounded-md" size={isMobile ? "md" : "lg"} />
                ),
                label: recordName,
                onClick: handleRecordClick,
            },
            {
                icon: <Icon icon={TbExternalLink} size={20} />,
                label: t("open_in_crm"),
                href: channelActions.crm.getHref(),
                target: "_blank",
                rel: "noopener noreferrer",
                onClick: () => {
                    if (!channelActions.crm.getHref()) {
                        console.warn("No record ID or module type available to open in CRM");
                    }
                },
            },
        ];

        if (canViewHotelCapacity) {
            items.push({
                icon: <Icon icon={TbBed} size={20} />,
                label: t("hotel_capacity_title"),
                onClick: handleHotelCapacityClick,
            });
        }

        items.push({
            icon: (
                <div className="relative w-6 h-6 flex items-center justify-center rounded-full overflow-hidden bg-muted/20">
                    <Icon icon={isMuted ? TbMicrophoneOff : TbMicrophone} size={20} className="relative z-10" />
                    {!isMuted && audioLevel.length > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 h-full flex items-end justify-center gap-px px-0.5">
                            {audioLevel.map((level, i) => (
                                <div
                                    key={i}
                                    className="flex-1 bg-green-500 transition-all duration-75 ease-out rounded-t"
                                    style={{
                                        height: `${Math.max(level * 100, 5)}%`,
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ),
            label: isMuted ? t("unmute_mic") : t("mute_mic"),
            onClick: handleMuteMic,
        });

        if (showDeviceSettings) {
            items.push({
                icon: <Icon icon={TbSettings} size={20} />,
                label: t("settings"),
                onClick: () => {
                    setIsSettingsOpen((prev) => !prev);
                },
            });
        }

        items.push({
            icon: <Icon icon={TbPhoneOff} size={20} />,
            label: t("hang_up"),
            onClick: handleHangupCall,
            className: "bg-destructive text-destructive-foreground border-destructive/50",
        });

        return items;
    }, [
        channelActions.crm,
        canViewHotelCapacity,
        frontImage,
        handleHangupCall,
        handleHotelCapacityClick,
        handleMuteMic,
        handleRecordClick,
        isMuted,
        audioLevel,
        recordName,
        showDeviceSettings,
        t,
        isMobile,
    ]);

    if (!isActive && !isVisible) {
        return null;
    }

    return (
        <motion.div
            className={cn(
                "fixed left-1/2 -translate-x-1/2 z-50",
                isMobile ? "bottom-[max(0.75rem,env(safe-area-inset-bottom))]" : "bottom-4",
                "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible && isActive
                    ? "opacity-100 translate-y-0 scale-100"
                    : "opacity-0 translate-y-8 scale-95 pointer-events-none"
            )}
            style={{ height: dockHeight, scrollbarWidth: "none" }}
        >
            {isMobile && (
                <motion.div
                    className={cn(
                        "absolute bottom-full left-1/2 -translate-x-1/2 mb-2",
                        "flex items-center justify-center",
                        "rounded-lg border border-border/30 bg-background/80 backdrop-blur-sm",
                        "px-3 py-1.5 shadow-md"
                    )}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <CallDurationTimer
                        startTime={callStartTime}
                        isActive={isActive}
                        size="lg"
                        className="text-primary"
                        monospace={true}
                    />
                </motion.div>
            )}
            <motion.div
                ref={settingsButtonRef}
                onMouseMove={
                    isMobile
                        ? undefined
                        : ({ pageX }) => {
                              isHovered.set(1);
                              mouseX.set(pageX);
                          }
                }
                onMouseLeave={
                    isMobile
                        ? undefined
                        : () => {
                              isHovered.set(0);
                              mouseX.set(Infinity);
                          }
                }
                className={cn(
                    "absolute bottom-2 left-1/2 -translate-x-1/2",
                    "flex items-end w-fit max-w-[calc(100vw-1rem)]",
                    isMobile ? "gap-1.5 px-2 overflow-x-auto" : "gap-3 px-4",
                    "rounded-2xl border border-border/30",
                    "bg-background/60 backdrop-blur-2xl",
                    "pb-2 shadow-[0_8px_32px_0_rgba(0,0,0,0.12)]"
                )}
                style={{ height: panelHeight }}
                role="toolbar"
                aria-label="Call dock"
            >
                {dockItems.map((item, index) => (
                    <DockItem
                        key={index}
                        onClick={item.onClick}
                        href={item.href}
                        target={item.target}
                        rel={item.rel}
                        className={cn(
                            item.className,
                            callButtonCooldown && index === dockItems.length - 1 && "opacity-50 cursor-not-allowed"
                        )}
                        mouseX={mouseX}
                        spring={spring}
                        distance={distance}
                        magnification={magnification}
                        baseItemSize={baseItemSize}
                    >
                        <DockIcon>{item.icon}</DockIcon>
                        <DockLabel>{item.label}</DockLabel>
                    </DockItem>
                ))}
                {!isMobile && (
                    <motion.div
                        className={cn(
                            "absolute -right-2 top-1/2 -translate-y-1/2 translate-x-full",
                            "ml-3 flex items-center justify-center",
                            "rounded-lg border border-border/30 bg-background/80 backdrop-blur-sm",
                            "px-3 py-1.5 shadow-md"
                        )}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <CallDurationTimer
                            startTime={callStartTime}
                            isActive={isActive}
                            size="lg"
                            className="text-primary"
                            monospace={true}
                        />
                    </motion.div>
                )}

                {/* Settings Popover - floats with the dock */}
                <AnimatePresence>
                    {isSettingsOpen && (
                        <motion.div
                            ref={settingsRef}
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className={cn(
                                "absolute bottom-full mb-2 right-0",
                                isMobile && "left-0 right-0",
                                "min-w-[200px] rounded-lg border border-border/30",
                                "bg-background/95 backdrop-blur-xl shadow-lg",
                                "p-2 z-50"
                            )}
                            onMouseLeave={isMobile ? undefined : () => setHoveredDeviceType(null)}
                        >
                            <div className="space-y-1">
                                {canSelectMicrophone && (
                                    <div
                                        className={cn(
                                            "relative rounded-md",
                                            isMobile ? "flex flex-col" : "flex items-center justify-between",
                                            hoveredDeviceType === "input"
                                                ? "bg-primary/10 text-primary"
                                                : "hover:bg-muted/50"
                                        )}
                                    >
                                        <div
                                            className={cn(
                                                "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors",
                                                !isMobile && "w-full"
                                            )}
                                            onMouseEnter={isMobile ? undefined : () => setHoveredDeviceType("input")}
                                            onClick={
                                                isMobile
                                                    ? () =>
                                                          setHoveredDeviceType((prev) =>
                                                              prev === "input" ? null : "input"
                                                          )
                                                    : undefined
                                            }
                                        >
                                            <div className="flex items-center gap-2">
                                                <Icon icon={TbMicrophone} />
                                                <span className="text-sm font-medium">{t("input")}</span>
                                            </div>
                                            <Icon icon={TbChevronRight} className="opacity-50" />
                                        </div>
                                        {hoveredDeviceType === "input" && (
                                            <motion.div
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                className={cn(
                                                    isMobile
                                                        ? "border-t border-border/30 px-1 pb-1"
                                                        : "absolute left-full top-0 ml-2 min-w-[220px] rounded-lg border border-border/30 bg-background/95 backdrop-blur-xl shadow-lg p-2 z-50"
                                                )}
                                            >
                                                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                                                    {mediaDevices
                                                        .filter((device) => device.kind === "audioinput")
                                                        .map((device) => (
                                                            <button
                                                                key={device.deviceId}
                                                                onClick={async () => {
                                                                    setSelectedInputDeviceId(device.deviceId);
                                                                    await setInputDevice(device.deviceId);
                                                                    setHoveredDeviceType(null);
                                                                }}
                                                                className={cn(
                                                                    "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md",
                                                                    "text-left text-sm transition-colors",
                                                                    selectedInputDeviceId === device.deviceId
                                                                        ? "bg-primary/10 text-primary"
                                                                        : "hover:bg-primary/10 hover:text-primary"
                                                                )}
                                                            >
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <Icon icon={TbMicrophone} />
                                                                    <span className="truncate">
                                                                        {device.label || "Unknown Device"}
                                                                    </span>
                                                                </div>
                                                                {selectedInputDeviceId === device.deviceId && (
                                                                    <Icon
                                                                        icon={TbCheck}
                                                                        className="text-primary shrink-0"
                                                                    />
                                                                )}
                                                            </button>
                                                        ))}
                                                    {mediaDevices.filter((device) => device.kind === "audioinput")
                                                        .length === 0 && (
                                                        <div className="px-3 py-2 text-sm text-muted-foreground">
                                                            {t("no_input_devices")}
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>
                                )}

                                {canSelectSpeaker && (
                                    <div
                                        className={cn(
                                            "relative rounded-md",
                                            isMobile ? "flex flex-col" : "flex items-center justify-between",
                                            hoveredDeviceType === "output"
                                                ? "bg-primary/10 text-primary"
                                                : "hover:bg-muted/50"
                                        )}
                                    >
                                        <div
                                            className={cn(
                                                "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors",
                                                !isMobile && "w-full"
                                            )}
                                            onMouseEnter={isMobile ? undefined : () => setHoveredDeviceType("output")}
                                            onClick={
                                                isMobile
                                                    ? () =>
                                                          setHoveredDeviceType((prev) =>
                                                              prev === "output" ? null : "output"
                                                          )
                                                    : undefined
                                            }
                                        >
                                            <div className="flex items-center gap-2">
                                                <Icon icon={TbSpeakerphone} />
                                                <span className="text-sm font-medium">{t("output")}</span>
                                            </div>
                                            <Icon icon={TbChevronRight} className="opacity-50" />
                                        </div>
                                        {hoveredDeviceType === "output" && (
                                            <motion.div
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                className={cn(
                                                    isMobile
                                                        ? "border-t border-border/30 px-1 pb-1"
                                                        : "absolute left-full top-0 ml-2 min-w-[220px] rounded-lg border border-border/30 bg-background/95 backdrop-blur-xl shadow-lg p-2 z-50"
                                                )}
                                            >
                                                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                                                    {mediaDevices
                                                        .filter((device) => device.kind === "audiooutput")
                                                        .map((device) => (
                                                            <button
                                                                key={device.deviceId}
                                                                onClick={async () => {
                                                                    setSelectedOutputDeviceId(device.deviceId);
                                                                    try {
                                                                        await Promise.all([
                                                                            setOutputDevice(device.deviceId),
                                                                            soundService.setOutputDevice(
                                                                                device.deviceId
                                                                            ),
                                                                        ]);
                                                                    } catch (error) {
                                                                        console.error(
                                                                            "Error setting output device:",
                                                                            error
                                                                        );
                                                                    } finally {
                                                                        setHoveredDeviceType(null);
                                                                    }
                                                                }}
                                                                className={cn(
                                                                    "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md",
                                                                    "text-left text-sm transition-colors",
                                                                    selectedOutputDeviceId === device.deviceId
                                                                        ? "bg-primary/10 text-primary"
                                                                        : "hover:bg-primary/10 hover:text-primary"
                                                                )}
                                                            >
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <Icon icon={TbSpeakerphone} />
                                                                    <span className="truncate">
                                                                        {device.label || "Unknown Device"}
                                                                    </span>
                                                                </div>
                                                                {selectedOutputDeviceId === device.deviceId && (
                                                                    <Icon
                                                                        icon={TbCheck}
                                                                        className="text-primary shrink-0"
                                                                    />
                                                                )}
                                                            </button>
                                                        ))}
                                                    {mediaDevices.filter((device) => device.kind === "audiooutput")
                                                        .length === 0 && (
                                                        <div className="px-3 py-2 text-sm text-muted-foreground">
                                                            {t("no_output_devices")}
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </motion.div>
    );
}
